export const APP_ACCESS_LOCK = "zappy-exclusive-app-access-v1";

/**
 * Browser-origin coordination only. This never grants account permissions.
 * The most recent tab always takes over (steal); the previous tab becomes
 * "displaced" and stops rendering the app. Forgotten or frozen background
 * tabs can therefore never strand the user on a blocked screen.
 */
export type AccessState = "checking" | "owned" | "displaced" | "unsupported";
export type LockManagerLike = Pick<LockManager, "request">;

export function acquireAppAccess(
  locks: LockManagerLike | undefined,
  onState: (state: AccessState) => void,
): () => void {
  let active = true;
  let release: (() => void) | undefined;
  if (!locks) {
    onState("unsupported");
    return () => { active = false; };
  }
  void locks.request(APP_ACCESS_LOCK, { mode: "exclusive", steal: true }, async (lock) => {
    if (!active) return;
    if (!lock) {
      onState("displaced");
      return;
    }
    await new Promise<void>((resolve) => {
      release = resolve;
      onState("owned");
    });
  }).catch((error: unknown) => {
    if (!active) return;
    const aborted = error instanceof Error && error.name === "AbortError";
    onState(aborted ? "displaced" : "unsupported");
  });
  return () => {
    active = false;
    release?.();
  };
}
